import { DraggableProduct } from "./ProductCard";

/**
 * Narrow shape of a product row returned by `categoriesApi.searchProducts`
 * / `categoriesApi.searchAccommodation`. The endpoint returns more fields
 * than this — we only care about the ones needed to build a draggable
 * card. Marked optional + unknown-friendly to avoid asserting on fields
 * the backend may omit per product type.
 */
/**
 * Live-rate booking keys returned by
 * `accommodation/availableAccommodationProductsByLocation` for
 * externally-sourced rooms (e.g. Hotelbeds, `externalSource: "HB"`). For
 * CMS/generic suppliers these are absent. They are NOT displayed — they are
 * carried through the trip item to the checkout payload so the room can be
 * booked downstream.
 */
export interface RawBookingKeys {
    defaultRateCode?: string;
    roomCode?: string;
    externalId?: string | number;
    externalSource?: string;
}
export interface RawSearchFare extends RawBookingKeys {
    defaultFare?: boolean;
    fareTypeId?: string | number;
    fareName?: string;
    productPricesDetailsId?: string | number;
    rrp?: number | string;
    numPax?: number | string;
    /**
     * For external-API suppliers the live room rates (each carrying the
     * booking keys) come back nested in `availability`. For CMS suppliers
     * this is just an integer count, so read defensively.
     */
    availability?: RawBookingKeys[] | number | unknown;
}

/**
 * Booking keys after normalisation — every field is a string (or undefined).
 * Distinct from {@link RawBookingKeys} whose `externalId` may arrive as a
 * number from the backend.
 */
export interface NormalisedBookingKeys {
    defaultRateCode?: string;
    roomCode?: string;
    externalId?: string;
    externalSource?: string;
}

/**
 * Pull the booking keys off a fare. They may sit directly on the fare or,
 * for external-API suppliers, inside the first `availability` row. Returns
 * normalised string fields (or undefined when the supplier doesn't provide
 * them, e.g. CMS rooms).
 */
export const extractBookingKeys = (fare: RawSearchFare): NormalisedBookingKeys => {
    const nested =
        Array.isArray(fare.availability) && fare.availability.length > 0
            ? (fare.availability[0] as RawBookingKeys)
            : undefined;
    const str = (v: unknown): string | undefined =>
        v === undefined || v === null || v === "" ? undefined : String(v);
    // Resolve each key independently: prefer the value on the fare itself,
    // then fall back to the first availability row. Suppliers vary in where
    // they place each key and may provide only a partial set, so we must NOT
    // gate the whole source choice on `defaultRateCode` being present —
    // otherwise a fare with e.g. only a top-level `roomCode` would be missed.
    const pick = (k: keyof RawBookingKeys): string | undefined =>
        str(fare[k]) ?? (nested ? str(nested[k]) : undefined);
    return {
        defaultRateCode: pick("defaultRateCode"),
        roomCode: pick("roomCode"),
        externalId: pick("externalId"),
        externalSource: pick("externalSource"),
    };
};
export interface RawSearchProduct {
    productId?: string | number;
    tdms_product_id?: number | string;
    tdmsProductId?: number | string;
    name?: string;
    productImagePath?: string;
    productLargeSizeImagePath?: string;
    productClass?: string | null;
    categories?: {
        activities?: string[];
        accommodationNames?: string[];
        accommodation?: string[];
    };
    durationDays?: number | string;
    durationNight?: number | string;
    rrp?: number | string;
    faresprices?: RawSearchFare[];
}

/**
 * The FT search endpoint returns short class codes (e.g. "A" for hotels,
 * "Y" for tours) but the rest of the trip planner — slice reducers,
 * SpanBand renderer, edit modal — keys off the literal string
 * "accommodation". Detect any accommodation signal and normalise so the
 * downstream code treats it uniformly.
 */
const normaliseProductClass = (raw: RawSearchProduct): string | null => {
    const original = raw.productClass ?? null;
    const isHotelCode = original === "A";
    const hasAccomCategory =
        (raw.categories?.accommodationNames?.length ?? 0) > 0 ||
        (raw.categories?.accommodation?.length ?? 0) > 0;
    if (isHotelCode || hasAccomCategory) return "accommodation";
    return original ?? raw.categories?.activities?.[0] ?? null;
};

/**
 * Best-effort number-of-days span for a search-result product. Mirrors
 * `deriveProductDays` in AvailabilityModal.tsx so dragged-in items match
 * what the user sees in the per-product availability popup. For
 * accommodation specifically: a hotel with no `durationNight` value still
 * implies a 1-night stay, so the minimum span is 2 days.
 */
const deriveSpanDays = (raw: RawSearchProduct, productClass: string | null): number => {
    const days = Number(raw.durationDays);
    const nights = Number(raw.durationNight);
    // A trip with N nights occupies N+1 calendar days (board day 1, sleep N
    // nights, depart day N+1). Some products label themselves "X Day Y Night"
    // where X equals Y (e.g. a "2 Day 2 Night" liveaboard whose itinerary runs
    // Day 1→Day 3); for those the marketing `durationDays` understates the real
    // calendar footprint, so take the larger of the day count and nights+1.
    const daySpan = Number.isFinite(days) && days > 0 ? days : 0;
    const nightSpan = Number.isFinite(nights) && nights > 0 ? nights + 1 : 0;
    // A hotel with no explicit nights still implies a 1-night stay → 2 days.
    const accomMin = productClass === "accommodation" ? 2 : 0;
    return Math.max(1, daySpan, nightSpan, accomMin);
};

export const mapToDraggable = (raw: RawSearchProduct): DraggableProduct | null => {
    const fares = raw.faresprices ?? [];
    const fare: RawSearchFare = fares.find((f) => f?.defaultFare) ?? fares[0] ?? {};
    const productId = String(raw.productId ?? "");
    if (!productId) return null;
    const productClass = normaliseProductClass(raw);
    const bookingKeys = extractBookingKeys(fare);
    return {
        productId,
        tdmsProductId: Number(raw.tdms_product_id ?? raw.tdmsProductId ?? productId) || 0,
        name: String(raw.name ?? "Unknown"),
        productImagePath: raw.productImagePath || raw.productLargeSizeImagePath,
        productClass,
        durationDays: deriveSpanDays(raw, productClass),
        rrp: Number(raw.rrp ?? fare.rrp ?? 0),
        fareTypeId: String(fare.fareTypeId ?? ""),
        fareName: fare.fareName ?? "",
        productPricesDetailsId: String(fare.productPricesDetailsId ?? ""),
        numPax: parseFloat(String(fare.numPax ?? 1).replace(/[^\d.-]/g, "")) || 1,
        // Booking keys (defaultRateCode etc.) — only set when the supplier
        // provides them; spread so absent keys stay undefined rather than "".
        ...bookingKeys,
    };
};

export const extractProducts = (data: unknown): RawSearchProduct[] => {
    const body = data as
        | {
              data?: {
                  products?: RawSearchProduct[];
                  results?: RawSearchProduct[];
              };
              products?: RawSearchProduct[];
              results?: RawSearchProduct[];
          }
        | undefined;
    return (
        body?.data?.products ??
        body?.data?.results ??
        body?.products ??
        body?.results ??
        []
    );
};
